import { useRef, useEffect, useState, useCallback } from 'react';
import { Player } from '@lordicon/react';
import styles from './secondScreenB.module.css';
import useScrollReveal from '../../hooks/useScrollReveal';
import moneybagIcon from '../../icons/moneybag.json';
import womanIcon from '../../icons/wired-outline-3141-female-jumping-hover-jump (1).json';
import businessIcon from '../../icons/wired-outline-1010-department-hover-pinch (1).json';
import suitcaseIcon from '../../icons/wired-outline-187-suitcase-morph-open.json';
import marketingIcon from '../../icons/wired-outline-1027-marketing-campaign-hover-pinch.json';
import hairClipIcon from '../../icons/wired-outline-1563-hair-clip-hover-pinch.json';
import eyelashIcon from '../../icons/wired-outline-1575-eyelash-hover-pinch.json';

/* ── Data ── */
const benefits = [
  { icon: suitcaseIcon, text: 'לנהל את החיים שלך בדיוק איך שבא לך - בלי שאף אחד יחליט עבורך' },
  { icon: moneybagIcon, text: 'להרוויח הרבה מעבר לשכר ממוצע, ותכלס? בזמנים שלך!' },
  { icon: womanIcon,    text: 'לקום כל בוקר לעבודה שאת באמת אוהבת ולהרגיש שאת מגשימה את עצמך סוף סוף' },
  { icon: businessIcon, text: 'לבנות את העתיד שלך - כזה שאת יודעת שאת בנית בידיים שלך' },
];

const quotes = [
  '"תעשי קורס ותוך חודשיים היומן מפוצץ"',
  '"תוך חודשיים ואת מרוויחה יותר מהייטקיסט"',
];

const successSteps = [
  {
    icon: marketingIcon,
    title: 'לא חיכו שהעבודה תגיע לבד',
    description: 'מי שבאמת רוצה להצליח יוזמת! דואגת לפרסם, לתרגל, לצלם ולשווק את עצמה ולהשיג את הלקוחות שלה - ולא מחכה שלקוחות יגיעו כמו קסם.',
  },
  {
    icon: hairClipIcon,
    title: 'תרגלו והתמידו כל יום חדש',
    description: 'מי שרוצה להפוך להיות מומחית בתחום חייבת לתרגל! טכניקה לא נבנית ביום ולא ביומיים - היא נבנית בעבודה ותרגול מתמשכים.',
  },
  {
    icon: eyelashIcon,
    title: 'לא ויתרו כשהיה קשה',
    description: 'הבנות שאת רואה שמצליחות בתחום? גם להן היו ימים קשים, ימים בלי לקוחות וימים שהדברים לא הסתדרו כמו שהן רצו - אבל אף אחת מהן לא ויתרה על החלום שלה!',
  },
];


/* ── Benefit Card (used inside timeline) ── */
function BenefitCard({ icon, text, index = 0 }) {
  const playerRef = useRef(null);

  const handleComplete = () => {
    setTimeout(() => {
      playerRef.current?.playFromBeginning();
    }, 2500);
  };

  useEffect(() => {
    playerRef.current?.playFromBeginning();
  }, []);

  return (
    <div className={styles.benefitItem}>
      <div className={styles.benefitIcon} data-sr={String(0.3 + index * 0.12)} data-sr-y="0" data-sr-scale="0.6">
        <Player
          icon={icon}
          ref={playerRef}
          size="100%"
          colorize="#724121"
          stroke="light"
          onComplete={handleComplete}
        />
      </div>
      <p className={styles.benefitText} data-sr={String(0.4 + index * 0.12)}>{text}</p>
    </div>
  );
}

/* ── Animated lordicon  -  plays when activated ── */
function LordIcon({ icon, colorize = '#FAF0EB', size = '100%', className, play }) {
  const ref = useRef(null);
  const hasPlayed = useRef(false);
  useEffect(() => {
    if (play && !hasPlayed.current) {
      hasPlayed.current = true;
      const t = setTimeout(() => ref.current?.playFromBeginning(), 100);
      return () => clearTimeout(t);
    }
    if (!play) {
      hasPlayed.current = false;
    }
  }, [play]);
  const handleComplete = () => {
    setTimeout(() => ref.current?.playFromBeginning(), 3500);
  };
  return (
    <div className={className}>
      <Player icon={icon} ref={ref} size={size} colorize={colorize} stroke="light" onComplete={handleComplete} />
    </div>
  );
}

/* ══════════════════════════════════
   INTERACTIVE SUCCESS TIMELINE  -  scroll-driven
   ══════════════════════════════════ */
function SuccessTimeline({ steps }) {
  const [activeStep, setActiveStep] = useState(-1);
  const stepRefs = useRef([]);
  const lineRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      if (!timelineRef.current) return;
      const viewMid = window.innerHeight * 0.6;
      let current = -1;
      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < viewMid) current = i;
      });
      setActiveStep(current);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!lineRef.current || !timelineRef.current) return;
    if (activeStep < 0) {
      lineRef.current.style.height = '0px';
      return;
    }
    const timelineRect = timelineRef.current.getBoundingClientRect();
    const targetEl = stepRefs.current[activeStep];
    if (!targetEl) return;
    const targetRect = targetEl.getBoundingClientRect();
    const h = targetRect.top - timelineRect.top + targetRect.height * 0.5;
    lineRef.current.style.height = `${Math.max(0, h)}px`;
  }, [activeStep]);

  const setStepRef = useCallback((el, i) => {
    stepRefs.current[i] = el;
  }, []);

  return (
    <div className={styles.successTimeline} ref={timelineRef}>
      <div className={styles.stTrack} />
      <div className={styles.stProgress} ref={lineRef} />

      {steps.map((step, i) => {
        const isActive = i <= activeStep;
        const isCurrent = i === activeStep;
        return (
          <div
            key={i}
            ref={el => setStepRef(el, i)}
            className={`${styles.stItem} ${isActive ? styles.stItemActive : ''}`}
          >
            <div className={`${styles.stCircle} ${isActive ? styles.stCircleActive : ''} ${isCurrent ? styles.stCircleCurrent : ''}`}>
              <LordIcon
                icon={step.icon}
                className={styles.stCircleIcon}
                colorize={isActive ? '#FAF0EB' : 'rgba(196, 126, 143, 0.4)'}
                play={isActive}
              />
            </div>

            <div className={`${styles.stCard} ${isActive ? styles.stCardActive : ''}`}>
              <div className={styles.stCardShine} />
              <h3 className={styles.stTitle}>{step.title}</h3>
              <p className={styles.stDesc}>{step.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════ */
function SecondScreenB() {
  const darkZoneRef = useRef(null);
  const lightZoneRef = useRef(null);
  useScrollReveal(darkZoneRef);
  useScrollReveal(lightZoneRef);
  return (
    <section className={styles.screen}>

      {/* ══════ ZONE 1: LIGHT / BEIGE ══════ */}
      <div className={styles.lightZone} ref={lightZoneRef}>
        {/* Glows */}
        <div className={styles.lightGlowTop} />
        <div className={styles.lightGlowBottom} />

        {/* Brown sparkles */}
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`${styles.brownSparkle} ${styles[`bs${i + 1}`]}`} />
        ))}

        <div className={styles.container}>
          {/* 1. Title */}
          <div className={styles.enterUp1}>
            <h2 className={styles.lightTitle}>אם הגעת לכאן</h2>
          </div>

          {/* 2. Description */}
          <div className={styles.enterUp2}>
            <p className={styles.lightDesc}>
              סימן שתמיד אהבת את תחום הביוטי אבל לאחרונה משהו גרם לך להתעניין לגביו קצת יותר ולהבין איך את הופכת אותו לקריירה
            </p>
          </div>

          {/* 3. Title */}
          <div className={styles.enterUp3}>
            <h2 className={styles.lightTitle}>חשוב לי שתדעי</h2>
          </div>

          {/* 4. Subtitle */}
          <div className={styles.enterUp4}>
            <p className={styles.discoverySubtitle}>שאת יכולה:</p>
          </div>

          {/* 5. Benefits  -  Timeline Cards */}
          <div className={styles.benefitsTimeline}>
            {/* Horizontal line */}
            <div className={styles.benefitsLine} />
            {benefits.map((b, i) => (
              <BenefitCard key={i} icon={b.icon} text={b.text} index={i} />
            ))}
          </div>

          {/* 6. Description */}
          <div className={styles.enterUp6}>
            <p className={styles.lightCatch}>
              תחום היופי הוא אחד מהתחומים הכי רווחיים היום
            </p>
          </div>

          {/* 7. Subtitle */}
          <div className={styles.enterUp7}>
            <p className={styles.lightSmall}>
              תחום שהביקוש אליו לא נגמר אף פעם!
            </p>
          </div>

          {/* 8. Catch phrase */}
          <div className={styles.enterUp8}>
            <p className={styles.lightCatch}>
              וזו חד משמעית יכולה להיות המציאות שלך!
            </p>
          </div>
        </div>
      </div>

      {/* ══════ ZONE 2: DARK / BROWN ══════ */}
      <div className={styles.darkZone} ref={darkZoneRef}>
        {/* Pink glows */}
        <div className={styles.darkGlowTopRight} />
        <div className={styles.darkGlowBottomLeft} />
        <div className={styles.darkGlowCenter} />

        {/* Pink sparkles */}
        {[...Array(15)].map((_, i) => (
          <div key={i} className={`${styles.pinkSparkle} ${styles[`ps${i + 1}`]}`} />
        ))}

        <div className={styles.container}>
          {/* 9. Title */}
            <h2 className={styles.darkTitle} data-sr="0">
              אבל <em className={styles.closingHighlight}>לא כל כך מהר..</em>
            </h2>

          {/* 10. Intro text */}
            <p className={styles.darkDesc} data-sr="0.08">
              בטח שמעת את המשפטים הבאים לא פעם ולא פעמיים
            </p>

          {/* 11. Quote ribbons */}
          <div className={styles.quotesGrid}>
            {quotes.map((q, i) => (
                <div key={i} className={styles.quoteRibbon} data-sr={String(0.12 + i * 0.15)} data-sr-y="0">
                  <div className={styles.quoteRibbonShine} />
                  <p className={styles.quoteRibbonText}>{q}</p>
                </div>
            ))}
          </div>

          {/* 12. Reality check */}
            <p className={styles.darkDesc} data-sr="0.08">
              נשמע מדהים, לא? רק שבמציאות{' '}
              <em className={styles.darkEm}>רוב הבנות שקנו את החלום הזה נשארו עם תעודה במגירה ובלי לקוחה אחת.</em>
            </p>

          {/* 13. Success lead */}
            <p className={styles.darkLead} data-sr="0.1">הבנות שבאמת הצליחו בתחום הן אלה ש:</p>

          {/* 14. Success factor timeline */}
          <SuccessTimeline steps={successSteps} />

          {/* ══════ ZONE 3: CLOSING (still dark) ══════ */}
            <div className={styles.closingPanel} data-sr="0.1">
              <h2 className={styles.closingTitle}>
                <em className={styles.closingHighlight}>והכי חשוב.</em>
              </h2>
              <br />
              <p className={styles.closingUnderlined}>
                הייתה להן מישהי בצד שלא נעלמה ברגע שהקורס נגמר.
              </p>
              <p className={styles.closingSmall}>
                אז אם את באמת רצינית לגבי החלום שלך חשוב לי שתדעי
              </p>
              <h2 className={styles.closingTitle}>
                <em className={styles.closingHighlight}>את במקום הנכון.</em>
              </h2>
            </div>
        </div>
      </div>

    </section>
  );
}

export default SecondScreenB;
